import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { isAxiosError } from "axios";

import { toast } from "react-toastify";

import { ReduxRootState } from "../../types/redux-store";
import { resolveReduxAppStore } from "../../entities/lib/redux-app-store";
import { selectAllAccounts } from "../../entities/accounts/table";
import { selectCurrentUser } from "../../entities/current-user";
import {
  findAccountByIdentity,
  instagramProfileCandidate,
} from "../../entities/accounts/libs/account-identity";
import {
  addAccount,
  replaceAccount,
  selectAccountById,
} from "../../entities/accounts/table";
import { refreshInstagramCampaignStatus } from "../instagram-campaign/refresh-campaign-status";
import {
  getUserFriendlyErrorMessage,
  UserFriendlyError,
} from "../../api/UserFriendlyError";
import { AccountType } from "../../shared/types/accounts";
import {
  addInstagramPassword,
  addInstagramAPIkey,
  getAccountFromError,
  getInstagramAccounts,
  preservedInstagramScopes,
  retryVerifyLogin,
  updateInstagramPassword,
  updateInstagramUsername,
} from "../../api/social-account-connect/instagram";
import { getOneAccount } from "../../api/accounts";
import { discardSessionHandle } from "../../api/social-account-connect/facebook/authorise-facebook";
import type { BusinessTokenType } from "../../api/social-account-connect/facebook/fb-sdk-utils";
import { updateLostPublishing } from "../instagram-campaign/publishing-lost";
import { demoAccountReconnectNote } from "../../pages/accounts/view/components/demo-account-reconnect-note";

export type CurrentDialog = "add-password" | "update-password" | "verify-login";
export type VerifyStep = 1 | 2 | 3;
/*
 * SKE-4807. `connected-publishing-lost` is a success: the update landed and
 * Insights work. It is also a warning, because the account held the Instagram
 * publishing permission before and the new login did not carry it back. Only
 * ever set while `instagramPublishAuthenticationRequired` is on.
 */
export type ReconnectStatus =
  "pending" | "connecting" | "connected" | "connected-publishing-lost" | Error;
type InstagramSession = Awaited<
  ReturnType<typeof getInstagramAccounts>
>["session"];
type ReconnectPage = Awaited<
  ReturnType<typeof getInstagramAccounts>
>["pages"][number];

interface Data {
  session: InstagramSession;
  itemsToReconnect: {
    page: Required<ReturnType<typeof normalizeReconnectPage>>;
    account: AccountType;
  }[];
  pagesWithNoInsta: Array<ReturnType<typeof normalizeReconnectPage>>;
  pagesWithNoSked: Array<Required<ReturnType<typeof normalizeReconnectPage>>>;
}

export type InstagramActionsState = {
  currentDialog: CurrentDialog | null;
  addPassword: {
    uiState: "form" | "polling" | "not-approved";
    submitting: boolean;
    submitError: Error | null;
    pollingAccountId: string | null;
  };
  updatePassword: {
    username: {
      submitting: boolean;
      submitError: Error | null;
    };
    password: {
      submitting: boolean;
      submitError: Error | null;
    };
  };
  verifyLogin: {
    step: VerifyStep;
    submitting: boolean;
    pollingAccountId: string | null;
  };
  insightsReconnect: {
    data: Data | null;
    isModalShown: boolean;
    reconnectStatuses: Record<string, ReconnectStatus>;
    loading: boolean;
  };
};

const initialState: InstagramActionsState = {
  currentDialog: null,
  addPassword: {
    uiState: "form",
    submitting: false,
    submitError: null,
    pollingAccountId: null,
  },
  updatePassword: {
    username: {
      submitting: false,
      submitError: null,
    },
    password: {
      submitting: false,
      submitError: null,
    },
  },
  verifyLogin: {
    step: 1,
    submitting: false,
    pollingAccountId: null,
  },
  insightsReconnect: {
    data: null,
    isModalShown: false,
    reconnectStatuses: {},
    loading: false,
  },
};

const featureKey = "instagramActions" as const;

export function dispatchAppAction(action: unknown) {
  return resolveReduxAppStore()?.dispatch(action);
}

function getFeatureState(state: ReduxRootState): InstagramActionsState {
  return (
    (
      state as ReduxRootState & {
        [featureKey]?: InstagramActionsState;
      }
    )[featureKey] ?? initialState
  );
}

function isGoingThroughLoginCheck(account: AccountType): boolean {
  return ["checking", "received", "queued"].includes(account.status);
}

function normalizeReconnectPage(page: ReconnectPage): ReconnectPage {
  return {
    ...page,
    page: page.page,
    ig: page.ig,
  };
}

const instagramActionsSlice = createSlice({
  name: featureKey,
  initialState,
  reducers: {
    openDialog(state, action: PayloadAction<CurrentDialog>) {
      state.currentDialog = action.payload;
    },
    closeDialogState(state) {
      state.currentDialog = null;
      state.addPassword = initialState.addPassword;
      state.updatePassword = initialState.updatePassword;
      state.verifyLogin = initialState.verifyLogin;
    },
    setVerifyLoginObservedAccount(state, action: PayloadAction<AccountType>) {
      const account = action.payload;

      if (isGoingThroughLoginCheck(account)) {
        state.verifyLogin.step = 2;
        return;
      }

      if (state.verifyLogin.step === 2) {
        state.verifyLogin.step = 3;
      }
    },
    startAddPasswordPolling(state, action: PayloadAction<string>) {
      state.addPassword.uiState = "polling";
      state.addPassword.pollingAccountId = action.payload;
    },
    stopAddPasswordPolling(state) {
      state.addPassword.pollingAccountId = null;
    },
    setAddPasswordNotApproved(state) {
      state.addPassword.uiState = "not-approved";
    },
    startVerifyLoginPolling(state, action: PayloadAction<string>) {
      state.verifyLogin.pollingAccountId = action.payload;
    },
    stopVerifyLoginPolling(state) {
      state.verifyLogin.pollingAccountId = null;
    },
    hideInsightsReconnectModal(state) {
      state.insightsReconnect.isModalShown = false;
    },
    resetInsightsReconnectState(state) {
      state.insightsReconnect = initialState.insightsReconnect;
    },
    setReconnectStatus(
      state,
      action: PayloadAction<{
        accountId: string;
        status: ReconnectStatus;
      }>
    ) {
      state.insightsReconnect.reconnectStatuses[action.payload.accountId] =
        action.payload.status;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(submitInstagramPassword.pending, (state) => {
        state.addPassword.submitting = true;
        state.addPassword.submitError = null;
      })
      .addCase(submitInstagramPassword.fulfilled, (state, action) => {
        state.addPassword.submitting = false;
        state.addPassword.uiState = "polling";
        state.addPassword.pollingAccountId = action.payload._id;
      })
      .addCase(submitInstagramPassword.rejected, (state, action) => {
        state.addPassword.submitting = false;
        state.addPassword.submitError =
          (action.error as Error | undefined) ?? new Error("unknown");
      })
      .addCase(updateInstagramUsernameThunk.pending, (state) => {
        state.updatePassword.username.submitting = true;
        state.updatePassword.username.submitError = null;
      })
      .addCase(updateInstagramUsernameThunk.fulfilled, (state) => {
        state.updatePassword.username.submitting = false;
        state.currentDialog = "verify-login";
      })
      .addCase(updateInstagramUsernameThunk.rejected, (state, action) => {
        state.updatePassword.username.submitting = false;
        state.updatePassword.username.submitError =
          (action.error as Error | undefined) ?? new Error("unknown");
      })
      .addCase(updateInstagramPasswordThunk.pending, (state) => {
        state.updatePassword.password.submitting = true;
        state.updatePassword.password.submitError = null;
      })
      .addCase(updateInstagramPasswordThunk.fulfilled, (state) => {
        state.updatePassword.password.submitting = false;
        state.currentDialog = "verify-login";
      })
      .addCase(updateInstagramPasswordThunk.rejected, (state, action) => {
        state.updatePassword.password.submitting = false;
        state.updatePassword.password.submitError =
          (action.error as Error | undefined) ?? new Error("unknown");
      })
      .addCase(retryVerifyLoginThunk.pending, (state) => {
        state.verifyLogin.submitting = true;
      })
      .addCase(retryVerifyLoginThunk.fulfilled, (state) => {
        state.verifyLogin.submitting = false;
      })
      .addCase(retryVerifyLoginThunk.rejected, (state) => {
        state.verifyLogin.submitting = false;
      })
      .addCase(loadInstagramInsightsReconnect.pending, (state) => {
        state.insightsReconnect.loading = true;
        state.insightsReconnect.reconnectStatuses = {};
      })
      .addCase(loadInstagramInsightsReconnect.fulfilled, (state, action) => {
        state.insightsReconnect.loading = false;
        state.insightsReconnect.data = action.payload;
        state.insightsReconnect.isModalShown = true;
      })
      .addCase(loadInstagramInsightsReconnect.rejected, (state) => {
        state.insightsReconnect.loading = false;
      })
      .addCase(reconnectInstagramInsightsAccount.pending, (state, action) => {
        state.insightsReconnect.reconnectStatuses[action.meta.arg.account._id] =
          "connecting";
      })
      .addCase(reconnectInstagramInsightsAccount.fulfilled, (state, action) => {
        state.insightsReconnect.reconnectStatuses[action.meta.arg.account._id] =
          action.meta.publishingLost
            ? "connected-publishing-lost"
            : "connected";
      })
      .addCase(reconnectInstagramInsightsAccount.rejected, (state, action) => {
        state.insightsReconnect.reconnectStatuses[action.meta.arg.account._id] =
          new UserFriendlyError(
            action.payload ?? action.error.message ?? "unknown"
          );
      });
  },
});

export const submitInstagramPassword = createAsyncThunk(
  "instagramActions/submitInstagramPassword",
  async (
    payload: {
      accountId: string;
      password: string;
      twoFactorKey?: string;
    },
    thunkApi
  ) => {
    try {
      const account = await addInstagramPassword(payload);
      thunkApi.dispatch(replaceAccount(account));
      return account;
    } catch (error) {
      toast.error(getUserFriendlyErrorMessage(error));
      throw error;
    }
  }
);

export const updateInstagramUsernameThunk = createAsyncThunk(
  "instagramActions/updateInstagramUsername",
  async (
    payload: {
      accountId: string;
      value: string;
    },
    thunkApi
  ) => {
    const account = await updateInstagramUsername({
      accountId: payload.accountId,
      username: payload.value,
    });
    thunkApi.dispatch(replaceAccount(account));
    return account;
  }
);

export const updateInstagramPasswordThunk = createAsyncThunk(
  "instagramActions/updateInstagramPassword",
  async (
    payload: {
      accountId: string;
      value: string;
    },
    thunkApi
  ) => {
    const account = await updateInstagramPassword({
      accountId: payload.accountId,
      password: payload.value,
    });
    thunkApi.dispatch(replaceAccount(account));
    return account;
  }
);

export const retryVerifyLoginThunk = createAsyncThunk(
  "instagramActions/retryVerifyLogin",
  async (account: AccountType, thunkApi) => {
    try {
      const nextAccount = await retryVerifyLogin(account._id);
      thunkApi.dispatch(replaceAccount(nextAccount));
      return nextAccount;
    } catch (error) {
      const erroredAccount = getAccountFromError(error as Error);

      if (erroredAccount) {
        thunkApi.dispatch(replaceAccount(erroredAccount));
        thunkApi.dispatch(instagramActionsActions.closeDialogState());
      }

      toast.error(getUserFriendlyErrorMessage(error));
      throw error;
    }
  }
);

export const loadInstagramInsightsReconnect = createAsyncThunk(
  "instagramActions/loadInstagramInsightsReconnect",
  async (arg: { businessTokenType?: BusinessTokenType } | void, thunkApi) => {
    const state = thunkApi.getState() as ReduxRootState;
    const accounts = selectAllAccounts(state);
    const user = selectCurrentUser(state);
    const itemsToReconnect: Data["itemsToReconnect"] = [];
    const pagesWithNoInsta: Data["pagesWithNoInsta"] = [];
    const pagesWithNoSked: Data["pagesWithNoSked"] = [];

    try {
      /*
       * SKE-4806. This pass reconnects whatever Facebook returns under one
       * login, so the permissions to keep are the union across the workspace's
       * accounts, not one account's. A narrower request here strips the
       * publishing permission from an account that had it, because
       * `addInstagramAPItoken` replaces the stored session wholesale.
       */
      const { session, pages } = await getInstagramAccounts({
        additionalScopes: [
          "instagram_manage_comments",
          ...preservedInstagramScopes(accounts),
        ],
        useShortPagesFbApi: user.preferences?.useShortPagesFbApi || false,
        ...(arg?.businessTokenType
          ? { businessTokenType: arg.businessTokenType }
          : {}),
      });

      for (const rawPage of pages) {
        const page = normalizeReconnectPage(rawPage);

        if (!page.ig) {
          pagesWithNoInsta.push(page);
          continue;
        }

        const account = findAccountByIdentity(
          accounts,
          instagramProfileCandidate(page.ig)
        );

        if (account) {
          itemsToReconnect.push({
            page: page as Required<typeof page>,
            account,
          });
        } else {
          pagesWithNoSked.push(page as Required<typeof page>);
        }
      }

      return {
        session,
        itemsToReconnect,
        pagesWithNoInsta,
        pagesWithNoSked,
      };
    } catch (error) {
      // getInstagramAccounts has already toasted the reason. Rejecting keeps
      // the "no matching accounts" modal closed for a login that never
      // returned any Pages.
      console.log("error from getInstagramAccounts", error);
      throw error;
    }
  }
);

export const reconnectInstagramInsightsAccount = createAsyncThunk<
  AccountType,
  {
    session: InstagramSession;
    page: Required<ReturnType<typeof normalizeReconnectPage>>;
    account: AccountType;
  },
  { rejectValue: string; fulfilledMeta: { publishingLost: boolean } }
>(
  "instagramActions/reconnectInstagramInsightsAccount",
  async (payload, thunkApi) => {
    /*
     * SKE-4807. The row as the store holds it BEFORE this update, rather than
     * the one captured when the dialog loaded: a sibling pass may have written
     * it since. `addInstagramAPIkey` refetches the row once the write lands,
     * so `account` below is the after half of the comparison.
     */
    const accountBefore =
      selectAccountById(
        thunkApi.getState() as ReduxRootState,
        payload.account._id
      ) ?? payload.account;

    try {
      const account = await addInstagramAPIkey(payload.account._id, {
        session: payload.session,
        page: payload.page as never,
      });

      thunkApi.dispatch(addAccount(account));

      return thunkApi.fulfillWithValue(account, {
        publishingLost: updateLostPublishing(accountBefore, account),
      });
    } catch (error) {
      // The server's reason (e.g. an expired connection), not axios's
      // "Request failed with status code 410".
      return thunkApi.rejectWithValue(
        getUserFriendlyErrorMessage(error, (error as Error)?.message)
      );
    }
  }
);

export const instagramActionsReducer = instagramActionsSlice.reducer;
export const instagramActionsActions = instagramActionsSlice.actions;

export const selectInstagramActionsState = createSelector(
  getFeatureState,
  (state) => state
);

export const selectInstagramCurrentDialog = createSelector(
  selectInstagramActionsState,
  (state) => state.currentDialog
);

export const selectAddPasswordState = createSelector(
  selectInstagramActionsState,
  (state) => state.addPassword
);

export const selectUpdatePasswordState = createSelector(
  selectInstagramActionsState,
  (state) => state.updatePassword
);

export const selectVerifyLoginState = createSelector(
  selectInstagramActionsState,
  (state) => state.verifyLogin
);

export const selectInstagramInsightsReconnectState = createSelector(
  selectInstagramActionsState,
  (state) => state.insightsReconnect
);

export const selectAnyInstagramActionInProgress = createSelector(
  selectInstagramActionsState,
  (state) => {
    const reconnecting = Object.values(
      state.insightsReconnect.reconnectStatuses
    ).includes("connecting");

    return (
      Boolean(state.currentDialog) ||
      state.insightsReconnect.isModalShown ||
      state.insightsReconnect.loading ||
      reconnecting
    );
  }
);

function isThunkActionOfType<TPayload>(
  result: unknown,
  type: string
): result is { type: string; payload: TPayload } {
  return (
    typeof result === "object" &&
    result !== null &&
    "type" in result &&
    (result as { type?: unknown }).type === type
  );
}

async function waitForNextPoll() {
  await new Promise((resolve) => {
    window.setTimeout(resolve, 1500);
  });
}

export async function pollInstagramAccountReview(accountId: string) {
  const store = resolveReduxAppStore();

  if (!store) {
    return;
  }

  store.dispatch(instagramActionsActions.startAddPasswordPolling(accountId));

  while (true) {
    const latestState = getFeatureState(store.getState() as ReduxRootState);

    if (latestState.addPassword.pollingAccountId !== accountId) {
      return;
    }

    try {
      const account = await getOneAccount(accountId);

      store.dispatch(replaceAccount(account));

      if (account.status === "notApproved") {
        store.dispatch(instagramActionsActions.stopAddPasswordPolling());
        store.dispatch(instagramActionsActions.setAddPasswordNotApproved());
        return;
      }

      if (account.status !== "reviewing") {
        store.dispatch(instagramActionsActions.stopAddPasswordPolling());

        if (account.status === "waiting") {
          store.dispatch(instagramActionsActions.openDialog("verify-login"));
        }

        return;
      }
    } catch {
      // Match develop polling behavior: transient fetch failures should not
      // permanently abort review/login polling.
    }

    await waitForNextPoll();
  }
}

export async function pollVerifyLoginAccount(accountId: string) {
  const store = resolveReduxAppStore();

  if (!store) {
    return;
  }

  store.dispatch(instagramActionsActions.startVerifyLoginPolling(accountId));

  while (true) {
    const latestState = getFeatureState(store.getState() as ReduxRootState);

    if (latestState.verifyLogin.pollingAccountId !== accountId) {
      return;
    }

    try {
      const account = await getOneAccount(accountId);

      store.dispatch(replaceAccount(account));
      store.dispatch(
        instagramActionsActions.setVerifyLoginObservedAccount(account)
      );

      if (!isGoingThroughLoginCheck(account)) {
        store.dispatch(instagramActionsActions.stopVerifyLoginPolling());
        return;
      }
    } catch {
      // Match develop polling behavior: transient fetch failures should not
      // permanently abort review/login polling.
    }

    await waitForNextPoll();
  }
}

const accountsRefreshingThroughLoginCheck = new Set<string>();

export async function refreshAccountUntilLoginCheckEnds(accountId: string) {
  if (accountsRefreshingThroughLoginCheck.has(accountId)) {
    return;
  }
  accountsRefreshingThroughLoginCheck.add(accountId);

  try {
    while (true) {
      await waitForNextPoll();
      try {
        const account = await getOneAccount(accountId);
        dispatchAppAction(replaceAccount(account));
        if (!isGoingThroughLoginCheck(account)) {
          return;
        }
      } catch (error) {
        const status = isAxiosError(error) ? error.response?.status : undefined;
        if (status === 401 || status === 403 || status === 404) {
          return;
        }
        // Other fetch failures are transient and keep polling, as the dialog poll does.
      }
    }
  } finally {
    accountsRefreshingThroughLoginCheck.delete(accountId);
  }
}

export function openAddPasswordDialog() {
  dispatchAppAction(instagramActionsActions.openDialog("add-password"));
}

export function openUpdatePasswordDialog() {
  dispatchAppAction(instagramActionsActions.openDialog("update-password"));
}

export function openVerifyLoginDialog() {
  dispatchAppAction(instagramActionsActions.openDialog("verify-login"));
}

export function openDialog(dialog: CurrentDialog) {
  dispatchAppAction(instagramActionsActions.openDialog(dialog));
}

export function closeDialog() {
  dispatchAppAction(instagramActionsActions.closeDialogState());
}

export function onSubmit(payload: {
  accountId: string;
  password: string;
  twoFactorKey?: string;
}) {
  const action = submitInstagramPassword(payload);
  const dispatchResult = dispatchAppAction(action);

  void Promise.resolve(dispatchResult).then((result: unknown) => {
    if (
      isThunkActionOfType<AccountType>(
        result,
        submitInstagramPassword.fulfilled.type
      )
    ) {
      void pollInstagramAccountReview(result.payload._id);
    }
  });

  return dispatchResult;
}

export function onSubmitUsername(payload: {
  accountId: string;
  value: string;
}) {
  return dispatchAppAction(updateInstagramUsernameThunk(payload));
}

export function onSubmitPassword(payload: {
  accountId: string;
  value: string;
}) {
  return dispatchAppAction(updateInstagramPasswordThunk(payload));
}

export function onRetryVerifyLoginReject() {
  dispatchAppAction(instagramActionsActions.closeDialogState());
}

export function startCheckFx(account: AccountType) {
  return dispatchAppAction(retryVerifyLoginThunk(account));
}

export function reconnectInsightsClicked(
  account?: AccountType,
  businessTokenType?: BusinessTokenType
) {
  if (account?.isMarketingDemo) {
    demoAccountReconnectNote.open(dispatchAppAction);
    return;
  }
  const dispatchResult = dispatchAppAction(
    businessTokenType
      ? loadInstagramInsightsReconnect({ businessTokenType })
      : loadInstagramInsightsReconnect()
  );

  void Promise.resolve(dispatchResult).then((result: unknown) => {
    if (
      !isThunkActionOfType<Data>(
        result,
        loadInstagramInsightsReconnect.fulfilled.type
      )
    ) {
      return;
    }

    const reconnects = result.payload.itemsToReconnect.map((item) =>
      Promise.resolve(
        dispatchAppAction(
          reconnectInstagramInsightsAccount({
            session: result.payload.session,
            page: item.page,
            account: item.account,
          })
        )
      )
    );

    /*
     * SKE-4790. One refresh for the whole pass, not one per account. An agency
     * reconnecting fifty accounts fans out fifty concurrent thunks, and
     * `invalidateQueries` cancels any refetch already in flight, so calling it
     * per account would restart the campaign status request fifty times.
     * Nothing is live until at least one account lands, so a pass where every
     * account failed leaves the cache alone.
     */
    void Promise.allSettled(reconnects).then((settled) => {
      const anyReconnected = settled.some(
        (outcome) =>
          outcome.status === "fulfilled" &&
          isThunkActionOfType<AccountType>(
            outcome.value,
            reconnectInstagramInsightsAccount.fulfilled.type
          )
      );

      if (anyReconnected) {
        refreshInstagramCampaignStatus();
      }
    });
  });

  return dispatchResult;
}

export function hideModal() {
  dispatchAppAction(instagramActionsActions.hideInsightsReconnectModal());
}

/**
 * SKE-5310. "Connect another way" from the list a System User login produced:
 * the same pass on the User-token configuration. The list's connection is
 * handed back once the new pass has its accounts; any account it already
 * reconnected keeps the grant, because the server will not revoke a grant a
 * connected account shares.
 */
export async function reconnectInsightsAnotherWay(
  session: InstagramSession
): Promise<void> {
  hideModal();
  const result = await reconnectInsightsClicked(undefined, "user");
  if (
    isThunkActionOfType<Data>(
      result,
      loadInstagramInsightsReconnect.fulfilled.type
    )
  ) {
    discardSessionHandle(session);
  }
}

export function observeVerifyLoginAccount(account: AccountType) {
  dispatchAppAction(
    instagramActionsActions.setVerifyLoginObservedAccount(account)
  );
}

export function startVerifyLoginPolling(accountId: string) {
  return pollVerifyLoginAccount(accountId);
}

export function stopVerifyLoginPolling() {
  dispatchAppAction(instagramActionsActions.stopVerifyLoginPolling());
}
